import { useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { useQueryClient } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { t } from 'i18next'

import { formatDateToStringIgnoreUTC } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { patch } from '~/modules/retailMedia/store/industry/campaign/actions'
import { useAppSelector } from '~/store/hooks'

export function useCampaignHeader(data: CampaignView) {
  const formRef = useRef<FormHandles>(null)
  const { lengthCentsCurrency } = useAppInfo()

  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [isEditMode, setIsEditMode] = useState(false)

  const goal = data.settings.goal
  const canEdit = useMemo(() => data?.canEditCampaign, [data?.canEditCampaign])

  const {
    campaign: {
      patch: { loading: isSavingLoading }
    }
  } = useAppSelector(state => state.retailMedia.industry)

  const initialData = useMemo(
    () => ({
      budget: {
        daily: data.settings.daily_budget
      },
      settings: {
        name: data.title,
        start_date: formatDateToStringIgnoreUTC(
          data?.raw?.start_at,
          'YYYY-MM-DD'
        ),
        end_date: data?.raw?.end_at
          ? formatDateToStringIgnoreUTC(data?.raw?.end_at, 'YYYY-MM-DD')
          : null,
        is_always_on: data.raw.end_at === null,
        age_restricted: !!data.settings?.age_restricted
      },
      raw: { ...data.raw }
    }),
    [data]
  )

  const handleSuccessSubmit = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['get-ads']
    })

    setIsEditMode(false)
  }, [queryClient])

  const handleSubmit = useCallback(
    (formData: FormDataCampaignUpdate) => {
      function parseCurrencyValue(value?: string) {
        if (!value) return null
        const numberValue = Number(value)
        return lengthCentsCurrency ? numberValue / 100 : numberValue
      }

      const valueDailyBudget = formData.budget?.daily?.numAsString

      const body = {
        settings: {
          daily_budget: parseCurrencyValue(valueDailyBudget),
          age_restricted: !!formData.settings?.age_restricted
        },
        name: formData.settings?.name,
        end_at: formData.settings?.end_date || null
      }

      dispatch(
        patch({
          body,
          id: data?.id,
          onSuccess: handleSuccessSubmit,
          successFeedbackMsg: t(
            'rm:campaignInformation.updated.success.message'
          ),
          values: {
            patchKey: 'budget'
          }
        })
      )
    },
    [data, dispatch, handleSuccessSubmit, lengthCentsCurrency]
  )

  return {
    isEditMode,
    setIsEditMode,
    goal,
    canEdit,
    initialData,
    handleSubmit,
    isSavingLoading,
    formRef
  }
}
